import { useEffect, useMemo, useRef, useState } from 'react';
import type { Analytics } from '../analytics/client';
import type { Identity } from '../analytics/useIdentity';
import { stampEarned } from '../../core/events';
import { cameFromTool, deriveStamps, newlyFilled, type StampCardVariant, type StampFacts } from '../../core/stamps';
import type { Listing } from '../../core/layouts';
import type { StampsIO } from '../stamps';
import StampCard from './StampCard';
import { FLAG_TIMEOUT_MS } from './PreviewLayoutExperiment';

type Ready = { variant: StampCardVariant; facts: StampFacts };

/**
 * Only this boundary knows the stamp card is an experiment (E17). Both arms wait for the
 * same three things — a signed-in visitor, a flag answer inside 1.5s, and a stamp read
 * that succeeded — so a failed read drops a visitor from both arms alike. Control reads
 * and draws nothing. Anything missing is today's page with no exposure. The read starts
 * only once the flag has assigned an arm, so unassigned visitors cost no PostHog query.
 */
export default function StampCardExperiment({ analytics, identity, listings, io, onToolOpen = () => {} }: {
  analytics: Analytics | null;
  identity: Identity | null | undefined;
  listings: readonly Listing[];
  io: StampsIO;
  onToolOpen?: (tool: string) => void;
}) {
  const [ready, setReady] = useState<Ready | null>(null);
  const [fresh, setFresh] = useState<ReadonlySet<string>>(new Set());
  const exposed = useRef(false);
  const userId = identity?.id;

  useEffect(() => {
    if (!analytics?.watchStampCardVariant || !userId || exposed.current) return;
    let live = true;
    let stop = () => {};
    let timeout = 0;
    // A promise settles once, so the first answer wins and a late or failed flag is null.
    const flag = new Promise<StampCardVariant | null>(resolve => {
      timeout = window.setTimeout(() => resolve(null), FLAG_TIMEOUT_MS);
      try { stop = analytics.watchStampCardVariant!(resolve); } catch { resolve(null); }
    });
    void flag.then(async v => {
      if (!v) return;
      const facts = await io.fetchFacts(cameFromTool(io.referrer()));
      if (live && facts) setReady({ variant: v, facts });
    });
    return () => { live = false; window.clearTimeout(timeout); stop(); };
  }, [analytics, userId, io]);

  const stamps = useMemo(() => (ready?.variant === 'stamps' ? deriveStamps(ready.facts, listings) : null), [ready, listings]);

  useEffect(() => {
    if (!ready || !analytics || !userId || exposed.current) return;
    exposed.current = true;
    // The assigned arm has committed before this effect.
    try { analytics.recordStampCardExposure?.(ready.variant); } catch { /* Keep the page usable. */ }
    if (!stamps) return;
    const read = io.readSeen(userId);
    if (!read) return;
    const earned = newlyFilled(stamps, read.seen);
    if (earned.length === 0) return;
    const earnedIds = new Set(earned.map(s => s.id));
    io.writeSeen(userId, new Set([...read.seen, ...earnedIds]));
    // A device's first view records what was already earned without announcing it, so the
    // launch is not a burst of stamps nobody earned that day.
    if (read.first) return;
    for (const stamp of earned) {
      try { analytics.track(stampEarned(stamp.id, stamp.tool)); } catch { /* Keep the page usable. */ }
    }
    setFresh(earnedIds);
  }, [analytics, io, ready, stamps, userId]);

  return stamps ? <StampCard stamps={stamps} fresh={fresh} onToolOpen={onToolOpen} /> : null;
}
